import { strToU8, zipSync } from "fflate";
import { marked, type Token, type Tokens } from "marked";

import type { RetrievedChunkSnapshot } from "../db/types";
import { CITATION_RE, parseCitationMatch } from "./citations";
import { DOCX_MIME } from "./docxEdits";

// Export Word (.docx) d'une réponse ou d'une conversation (Figma 14.4) : un
// vrai document OOXML, construit dans le navigateur, sans serveur. Le texte
// garde ses titres, listes, tableaux, gras et liens ; chaque citation
// [N: "…"] devient un renvoi « [N] », et la liste des sources suit la réponse :
// le document, l'endroit, le lien et les phrases citées. Rachel demandait
// « notes de bas de page ou liste en fin de document » : la liste, parce
// qu'une réponse cite souvent le même passage plusieurs fois.

export interface DocxTurn {
  /** La question, absente pour l'export d'une seule réponse. */
  question?: string;
  answer: string;
  sources: RetrievedChunkSnapshot[];
  /** Mention sous la réponse (« Réponse produite avec Apertus… »). */
  note?: string;
}

/** Une image à insérer là où le texte porte « @@docx-image:N@@ » (N = index). */
export interface DocxImage {
  png: Uint8Array;
  /** Taille en pixels (96 dpi) ; réduite à la largeur du texte si besoin. */
  width: number;
  height: number;
}

export const IMAGE_PLACEHOLDER = /^@@docx-image:(\d+)@@$/;

export interface DocxLabels {
  question: string;
  answer: string;
  sources: string;
  /** Sous-titre : « Exporté de PolyChat le 20 octobre 2026 ». */
  meta: string;
  file: (name: string) => string;
  page: (n: number) => string;
}

const W_NS =
  'xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"';
const DRAWING_NS =
  'xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture"';
/** Largeur du texte (A4, marges de 2 cm), en EMU : 9638 twips. */
const TEXT_WIDTH_EMU = Math.round((9638 / 1440) * 914400);
const EMU_PER_PX = 914400 / 96;

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    // Caractères de contrôle interdits en XML 1.0.
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, "");
}

interface RunStyle {
  bold?: boolean;
  italic?: boolean;
  code?: boolean;
  strike?: boolean;
  link?: boolean;
  sup?: boolean;
  color?: string;
}

function run(text: string, st: RunStyle = {}): string {
  if (!text) return "";
  const props = [
    st.link ? '<w:rStyle w:val="Hyperlink"/>' : "",
    st.code ? '<w:rFonts w:ascii="Consolas" w:hAnsi="Consolas" w:cs="Consolas"/>' : "",
    st.bold ? "<w:b/>" : "",
    st.italic ? "<w:i/>" : "",
    st.strike ? "<w:strike/>" : "",
    st.color ? `<w:color w:val="${st.color}"/>` : "",
    st.sup ? '<w:vertAlign w:val="superscript"/>' : "",
  ].join("");
  // Les sauts de ligne deviennent des <w:br/>, les tabulations des <w:tab/> :
  // Word n'honore pas une tabulation brute dans un <w:t>.
  const rPr = props ? `<w:rPr>${props}</w:rPr>` : "";
  return text
    .split("\n")
    .map((part, i) => {
      const segments = part
        .split("\t")
        .map((s) => (s ? `<w:t xml:space="preserve">${esc(s)}</w:t>` : ""))
        .join("<w:tab/>");
      return `<w:r>${rPr}${i > 0 ? "<w:br/>" : ""}${segments}</w:r>`;
    })
    .join("");
}

/** Mise en forme d'un texte versé dans un modèle Word (docxTemplates.ts) :
 *  les styles du modèle plutôt que les nôtres, et son retrait de gauche. */
export interface DocxLook {
  /** Nos noms de style (Heading2, Quote…) → ceux du modèle. */
  styles?: Record<string, string>;
  /** Style des paragraphes ordinaires. */
  body?: string;
  /** Retrait de gauche du texte du modèle (twips), ajouté aux listes et tableaux. */
  indent?: number;
  /** Largeur d'un tableau (twips) ; sinon toute la largeur du texte. */
  tableWidth?: number;
}

class Builder {
  body: string[] = [];
  rels: { id: string; target: string }[] = [];
  images: DocxImage[];
  look: DocxLook;
  /** Images effectivement placées, dans l'ordre de leur relation. */
  media: { id: string; name: string; png: Uint8Array }[] = [];

  constructor(images: DocxImage[] = [], look: DocxLook = {}) {
    this.images = images;
    this.look = look;
  }

  style(name: string): string {
    return this.look.styles?.[name] ?? name;
  }

  /** Une image centrée, à la largeur du texte au plus. */
  image(index: number): void {
    const img = this.images[index];
    if (!img) return;
    const n = this.media.length + 1;
    const id = `rIdImg${n}`;
    const name = `image${n}.png`;
    this.media.push({ id, name, png: img.png });
    const scale = Math.min(1, TEXT_WIDTH_EMU / Math.max(1, img.width * EMU_PER_PX));
    const cx = Math.round(img.width * EMU_PER_PX * scale);
    const cy = Math.round(img.height * EMU_PER_PX * scale);
    this.body.push(
      `<w:p><w:pPr><w:jc w:val="center"/></w:pPr><w:r><w:drawing><wp:inline distT="0" distB="0" distL="0" distR="0"><wp:extent cx="${cx}" cy="${cy}"/><wp:docPr id="${n}" name="${name}"/><a:graphic><a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/picture"><pic:pic><pic:nvPicPr><pic:cNvPr id="${n}" name="${name}"/><pic:cNvPicPr/></pic:nvPicPr><pic:blipFill><a:blip r:embed="${id}"/><a:stretch><a:fillRect/></a:stretch></pic:blipFill><pic:spPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="${cx}" cy="${cy}"/></a:xfrm><a:prstGeom prst="rect"><a:avLst/></a:prstGeom></pic:spPr></pic:pic></a:graphicData></a:graphic></wp:inline></w:drawing></w:r></w:p>`,
    );
  }

  link(url: string): string {
    const found = this.rels.find((r) => r.target === url);
    if (found) return found.id;
    const id = `rIdLink${this.rels.length + 1}`;
    this.rels.push({ id, target: url });
    return id;
  }

  inline(tokens: Token[] | undefined, st: RunStyle = {}): string {
    if (!tokens) return "";
    return tokens
      .map((tok) => {
        switch (tok.type) {
          case "strong":
            return this.inline((tok as Tokens.Strong).tokens, { ...st, bold: true });
          case "em":
            return this.inline((tok as Tokens.Em).tokens, { ...st, italic: true });
          case "del":
            return this.inline((tok as Tokens.Del).tokens, { ...st, strike: true });
          case "codespan":
            return run(unescapeHtml((tok as Tokens.Codespan).text), { ...st, code: true });
          case "br":
            return "<w:r><w:br/></w:r>";
          case "link": {
            const l = tok as Tokens.Link;
            if (!/^https?:\/\//i.test(l.href)) return this.inline(l.tokens, st);
            return `<w:hyperlink r:id="${this.link(l.href)}">${this.inline(l.tokens, { ...st, link: true })}</w:hyperlink>`;
          }
          case "text": {
            const t = tok as Tokens.Text;
            return t.tokens ? this.inline(t.tokens, st) : run(unescapeHtml(t.text), st);
          }
          case "escape":
            return run((tok as Tokens.Escape).text, st);
          case "html":
            return run(stripTags((tok as Tokens.HTML).text), st);
          default:
            return "raw" in tok ? run(unescapeHtml(String(tok.raw)), st) : "";
        }
      })
      .join("");
  }

  para(runs: string, opts: { style?: string; indent?: number; hanging?: number; keepNext?: boolean } = {}): void {
    const style = opts.style ? this.style(opts.style) : this.look.body;
    const p = [
      style ? `<w:pStyle w:val="${style}"/>` : "",
      opts.keepNext ? "<w:keepNext/>" : "",
      opts.indent !== undefined
        ? `<w:ind w:left="${opts.indent + (this.look.indent ?? 0)}"${opts.hanging ? ` w:hanging="${opts.hanging}"` : ""}/>`
        : "",
    ].join("");
    this.body.push(`<w:p>${p ? `<w:pPr>${p}</w:pPr>` : ""}${runs}</w:p>`);
  }

  blocks(tokens: Token[], level = 0): void {
    for (const tok of tokens) {
      switch (tok.type) {
        case "heading": {
          const h = tok as Tokens.Heading;
          this.para(this.inline(h.tokens), { style: `Heading${Math.min(Math.max(h.depth, 1) + 1, 4)}`, keepNext: true });
          break;
        }
        case "paragraph":
        case "text": {
          const t = tok as Tokens.Paragraph | Tokens.Text;
          const placeholder = IMAGE_PLACEHOLDER.exec(t.text.trim());
          if (placeholder) {
            this.image(Number(placeholder[1]));
            break;
          }
          this.para(t.tokens ? this.inline(t.tokens) : run(unescapeHtml(t.text)), level ? { indent: 360 * level } : {});
          break;
        }
        case "list": {
          const l = tok as Tokens.List;
          const start = typeof l.start === "number" ? l.start : 1;
          l.items.forEach((item, i) => this.listItem(item, l.ordered ? `${start + i}.` : "•", level));
          break;
        }
        case "blockquote":
          for (const b of (tok as Tokens.Blockquote).tokens) {
            if (b.type === "paragraph") this.para(this.inline((b as Tokens.Paragraph).tokens), { style: "Quote" });
            else this.blocks([b], level + 1);
          }
          break;
        case "code":
          this.para(run((tok as Tokens.Code).text, { code: true }), { style: "Code" });
          break;
        case "table":
          this.table(tok as Tokens.Table);
          break;
        case "hr":
          this.para("", { style: "Rule" });
          break;
        case "html": {
          const text = stripTags((tok as Tokens.HTML).text).trim();
          if (text) this.para(run(text));
          break;
        }
        default:
          break;
      }
    }
  }

  listItem(item: Tokens.ListItem, marker: string, level: number): void {
    const indent = 360 * (level + 1) + 60;
    let first = true;
    for (const b of item.tokens) {
      if ((b.type === "text" || b.type === "paragraph") && first) {
        const inner = "tokens" in b && b.tokens ? this.inline(b.tokens as Token[]) : run(unescapeHtml((b as Tokens.Text).text));
        const check = item.task ? (item.checked ? "☑ " : "☐ ") : "";
        this.para(run(`${marker}\t${check}`) + inner, { indent, hanging: 360 });
        first = false;
      } else if (b.type === "list") {
        this.blocks([b], level + 1);
      } else {
        this.blocks([b], level + 1);
      }
    }
    if (first) this.para(run(`${marker}\t`), { indent, hanging: 360 });
  }

  table(t: Tokens.Table): void {
    // Dans un modèle, le texte des cellules prend le style du corps, sans son retrait.
    const cellPPr = this.look.body
      ? `<w:pStyle w:val="${this.look.body}"/><w:spacing w:before="40" w:after="40"/><w:ind w:left="0" w:right="0"/><w:jc w:val="left"/>`
      : '<w:spacing w:before="40" w:after="40"/>';
    const cell = (c: Tokens.TableCell, header: boolean) =>
      `<w:tc><w:tcPr><w:tcW w:w="0" w:type="auto"/>${header ? '<w:shd w:val="clear" w:color="auto" w:fill="F2F2F2"/>' : ""}</w:tcPr><w:p><w:pPr>${cellPPr}</w:pPr>${this.inline(c.tokens, header ? { bold: true } : {})}</w:p></w:tc>`;
    const rows = [
      `<w:tr><w:trPr><w:tblHeader/></w:trPr>${t.header.map((c) => cell(c, true)).join("")}</w:tr>`,
      ...t.rows.map((r) => `<w:tr>${r.map((c) => cell(c, false)).join("")}</w:tr>`),
    ];
    const border = '<w:top w:val="single" w:sz="4" w:color="BFBFBF"/><w:left w:val="single" w:sz="4" w:color="BFBFBF"/><w:bottom w:val="single" w:sz="4" w:color="BFBFBF"/><w:right w:val="single" w:sz="4" w:color="BFBFBF"/><w:insideH w:val="single" w:sz="4" w:color="BFBFBF"/><w:insideV w:val="single" w:sz="4" w:color="BFBFBF"/>';
    this.body.push(
      `<w:tbl><w:tblPr>${this.look.tableWidth ? `<w:tblW w:w="${this.look.tableWidth}" w:type="dxa"/>` : '<w:tblW w:w="5000" w:type="pct"/>'}${this.look.indent ? `<w:tblInd w:w="${this.look.indent}" w:type="dxa"/>` : ""}<w:tblBorders>${border}</w:tblBorders><w:tblCellMar><w:left w:w="80" w:type="dxa"/><w:right w:w="80" w:type="dxa"/></w:tblCellMar></w:tblPr>${rows.join("")}</w:tbl>`,
    );
    this.para("");
  }
}

function stripTags(html: string): string {
  return unescapeHtml(html.replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]+>/g, ""));
}

function unescapeHtml(s: string): string {
  return s
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    // Le modèle écrit parfois « &nbsp; » pour une ligne vide.
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&");
}

/** Le texte d'une réponse prêt pour Word : citations réduites à « [N] »,
 *  blocs de fichier remplacés par leur nom. Rend aussi, par numéro cité, les
 *  phrases citées. */
export function prepareAnswer(
  markdown: string,
  fileLabel: (name: string) => string,
): { markdown: string; quotes: Map<number, string[]> } {
  const quotes = new Map<number, string[]>();
  let md = markdown.replace(/````file:([^\n`]+)\n[\s\S]*?\n````/g, (_m, name: string) => `*${fileLabel(name.trim())}*`);
  md = md.replace(new RegExp(CITATION_RE.source, "g"), (...m) => {
    const parsed = parseCitationMatch(m as unknown as RegExpExecArray);
    for (const n of parsed.refs) {
      if (!parsed.quote) continue;
      const list = quotes.get(n) ?? [];
      if (!list.includes(parsed.quote)) list.push(parsed.quote);
      quotes.set(n, list);
    }
    return `[${parsed.refs.join(", ")}]`;
  });
  return { markdown: md, quotes };
}

/** Numéros [N] effectivement cités dans le texte préparé. */
function citedNumbers(markdown: string): number[] {
  const out = new Set<number>();
  for (const m of markdown.matchAll(/\[(\d+(?:\s*,\s*\d+)*)\]/g)) {
    for (const n of m[1].split(/\s*,\s*/)) out.add(Number(n));
  }
  return [...out].sort((a, b) => a - b);
}

function sourcesBlock(b: Builder, turn: DocxTurn, md: string, quotes: Map<number, string[]>, labels: DocxLabels): void {
  const byNumber = new Map(turn.sources.map((s) => [s.number, s]));
  const nums = citedNumbers(md).filter((n) => byNumber.has(n));
  if (nums.length === 0) return;
  b.para(run(labels.sources), { style: "Heading4", keepNext: true });
  for (const n of nums) {
    const s = byNumber.get(n)!;
    const section = s.section?.split(" > ").pop()?.trim();
    const where = [section, !s.source || s.source === "local" || s.page > 0 ? labels.page(s.page) : ""].filter(Boolean).join(", ");
    let runs = run(`[${n}]\t`, { bold: true }) + run(s.docName) + (where ? run(` · ${where}`) : "");
    if (s.sourceUrl && /^https?:\/\//i.test(s.sourceUrl)) {
      runs += run(" · ") + `<w:hyperlink r:id="${b.link(s.sourceUrl)}">${run(s.sourceUrl, { link: true })}</w:hyperlink>`;
    }
    b.para(runs, { style: "Source", indent: 480, hanging: 480 });
    for (const q of quotes.get(n) ?? []) b.para(run(`« ${q} »`, { italic: true, color: "595959" }), { style: "Source", indent: 480 });
  }
}

/** Le .docx d'une ou plusieurs réponses. `images` : les PNG que les
 *  réponses placent par « @@docx-image:N@@ » (schémas Mermaid rendus). */
export function buildDocx(title: string, turns: DocxTurn[], labels: DocxLabels, images: DocxImage[] = []): Uint8Array {
  const b = new Builder(images);
  b.para(run(title), { style: "Title" });
  b.para(run(labels.meta), { style: "Meta" });
  for (const turn of turns) {
    if (turn.question !== undefined) {
      b.para(run(labels.question), { style: "Heading2", keepNext: true });
      for (const line of turn.question.split(/\n{2,}/)) b.para(run(line), { style: "Asked" });
      b.para(run(labels.answer), { style: "Heading2", keepNext: true });
    }
    const { markdown, quotes } = prepareAnswer(turn.answer, labels.file);
    b.blocks(marked.lexer(markdown));
    if (turn.note) b.para(run(turn.note), { style: "Meta" });
    sourcesBlock(b, turn, markdown, quotes, labels);
  }
  return pack(b);
}

/** Un document Word créé par le modèle (bloc file:….docx), en vrai .docx :
 *  le texte tel quel, sans titre ni sources ajoutés (spec §11.1). */
export function markdownToDocx(markdown: string): Uint8Array {
  const b = new Builder([]);
  b.blocks(marked.lexer(markdown));
  return pack(b);
}

/** Les paragraphes WordprocessingML d'un texte markdown, à verser dans un
 *  modèle, et les liens qu'ils appellent (relations à ajouter au modèle). */
export function markdownToDocxXml(markdown: string, look: DocxLook): { xml: string; links: { id: string; target: string }[] } {
  const b = new Builder([], look);
  b.blocks(marked.lexer(markdown));
  return { xml: b.body.join(""), links: b.rels };
}

function pack(b: Builder): Uint8Array {
  const document = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document ${W_NS} ${DRAWING_NS}><w:body>${b.body.join("")}<w:sectPr><w:pgSz w:w="11906" w:h="16838"/><w:pgMar w:top="1134" w:right="1134" w:bottom="1134" w:left="1134" w:header="567" w:footer="567" w:gutter="0"/></w:sectPr></w:body></w:document>`;

  const docRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rIdStyles" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>${b.rels
    .map((r) => `<Relationship Id="${r.id}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/hyperlink" Target="${esc(r.target)}" TargetMode="External"/>`)
    .join("")}${b.media
    .map((m) => `<Relationship Id="${m.id}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="media/${m.name}"/>`)
    .join("")}</Relationships>`;

  const files: Record<string, Uint8Array> = {};
  for (const m of b.media) files[`word/media/${m.name}`] = m.png;
  return zipSync({
    ...files,
    "[Content_Types].xml": strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Default Extension="png" ContentType="image/png"/><Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/><Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/></Types>`),
    "_rels/.rels": strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/></Relationships>`),
    "word/document.xml": strToU8(document),
    "word/_rels/document.xml.rels": strToU8(docRels),
    "word/styles.xml": strToU8(STYLES),
  });
}

const STYLES = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:styles ${W_NS}>
<w:docDefaults><w:rPrDefault><w:rPr><w:rFonts w:ascii="Calibri" w:hAnsi="Calibri" w:cs="Calibri" w:eastAsia="Calibri"/><w:sz w:val="22"/><w:szCs w:val="22"/><w:lang w:val="fr-CH"/></w:rPr></w:rPrDefault><w:pPrDefault><w:pPr><w:spacing w:after="120" w:line="276" w:lineRule="auto"/></w:pPr></w:pPrDefault></w:docDefaults>
<w:style w:type="paragraph" w:default="1" w:styleId="Normal"><w:name w:val="Normal"/><w:qFormat/></w:style>
<w:style w:type="paragraph" w:styleId="Title"><w:name w:val="Title"/><w:basedOn w:val="Normal"/><w:next w:val="Normal"/><w:qFormat/><w:pPr><w:spacing w:after="60"/></w:pPr><w:rPr><w:sz w:val="36"/><w:szCs w:val="36"/></w:rPr></w:style>
<w:style w:type="paragraph" w:styleId="Meta"><w:name w:val="Meta"/><w:basedOn w:val="Normal"/><w:pPr><w:spacing w:after="360"/></w:pPr><w:rPr><w:color w:val="707070"/><w:sz w:val="20"/></w:rPr></w:style>
<w:style w:type="paragraph" w:styleId="Heading1"><w:name w:val="heading 1"/><w:basedOn w:val="Normal"/><w:next w:val="Normal"/><w:qFormat/><w:pPr><w:keepNext/><w:spacing w:before="360" w:after="120"/><w:outlineLvl w:val="0"/></w:pPr><w:rPr><w:b/><w:sz w:val="32"/></w:rPr></w:style>
<w:style w:type="paragraph" w:styleId="Heading2"><w:name w:val="heading 2"/><w:basedOn w:val="Normal"/><w:next w:val="Normal"/><w:qFormat/><w:pPr><w:keepNext/><w:spacing w:before="320" w:after="100"/><w:outlineLvl w:val="1"/></w:pPr><w:rPr><w:b/><w:color w:val="B51F1F"/><w:sz w:val="26"/></w:rPr></w:style>
<w:style w:type="paragraph" w:styleId="Heading3"><w:name w:val="heading 3"/><w:basedOn w:val="Normal"/><w:next w:val="Normal"/><w:qFormat/><w:pPr><w:keepNext/><w:spacing w:before="240" w:after="80"/><w:outlineLvl w:val="2"/></w:pPr><w:rPr><w:b/><w:sz w:val="24"/></w:rPr></w:style>
<w:style w:type="paragraph" w:styleId="Heading4"><w:name w:val="heading 4"/><w:basedOn w:val="Normal"/><w:next w:val="Normal"/><w:qFormat/><w:pPr><w:keepNext/><w:spacing w:before="200" w:after="60"/><w:outlineLvl w:val="3"/></w:pPr><w:rPr><w:b/><w:sz w:val="22"/></w:rPr></w:style>
<w:style w:type="paragraph" w:styleId="Asked"><w:name w:val="Question"/><w:basedOn w:val="Normal"/><w:pPr><w:shd w:val="clear" w:color="auto" w:fill="EDEDED"/><w:ind w:left="120" w:right="120"/></w:pPr></w:style>
<w:style w:type="paragraph" w:styleId="Quote"><w:name w:val="Quote"/><w:basedOn w:val="Normal"/><w:qFormat/><w:pPr><w:pBdr><w:left w:val="single" w:sz="12" w:space="8" w:color="C1C1C1"/></w:pBdr><w:ind w:left="240"/></w:pPr><w:rPr><w:color w:val="595959"/></w:rPr></w:style>
<w:style w:type="paragraph" w:styleId="Code"><w:name w:val="Code"/><w:basedOn w:val="Normal"/><w:pPr><w:shd w:val="clear" w:color="auto" w:fill="F4F4F4"/><w:spacing w:after="120" w:line="240" w:lineRule="auto"/></w:pPr><w:rPr><w:rFonts w:ascii="Consolas" w:hAnsi="Consolas" w:cs="Consolas"/><w:sz w:val="19"/></w:rPr></w:style>
<w:style w:type="paragraph" w:styleId="Source"><w:name w:val="Source"/><w:basedOn w:val="Normal"/><w:pPr><w:spacing w:after="60"/></w:pPr><w:rPr><w:sz w:val="20"/></w:rPr></w:style>
<w:style w:type="paragraph" w:styleId="Rule"><w:name w:val="Rule"/><w:basedOn w:val="Normal"/><w:pPr><w:pBdr><w:bottom w:val="single" w:sz="6" w:space="1" w:color="D5D5D5"/></w:pBdr></w:pPr></w:style>
<w:style w:type="character" w:styleId="Hyperlink"><w:name w:val="Hyperlink"/><w:rPr><w:color w:val="0563C1"/><w:u w:val="single"/></w:rPr></w:style>
</w:styles>`;

export function sanitizeDocxName(name: string): string {
  return (
    name
      .replace(/[\\/:*?"<>|]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 80) || "PolyChat"
  );
}

export function downloadDocx(bytes: Uint8Array, filename: string): void {
  const blob = new Blob([bytes as Uint8Array<ArrayBuffer>], { type: DOCX_MIME });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
